import * as React from "react";
import TeamsBaseComponent, {ITeamsBaseComponentState} from "msteams-react-base-component";
import {TextField} from "office-ui-fabric-react/lib/TextField";
import {
    PaginatedFilingLocations,
    FilingLocation,
    DmsSearch,
    UploadedFileInfo,
    DmsCaseInfo,
    CheckoutFolderInfo,
    DmsStorageType,
    EllipsisOptions,
    TabType,
    FilterOption,
    LinkType,
} from "../models";
import * as microsoftTeams from "@microsoft/teams-js";
import {IIconProps, Icon} from "office-ui-fabric-react/lib/Icon";
import "./styles.css";
import {post} from "../api";
import {Spinner, SpinnerSize} from "office-ui-fabric-react/lib/Spinner";
import {ISearchBoxStyles, SearchBox} from "office-ui-fabric-react/lib/SearchBox";
import LocationItemsListTabView from "./LocationItemsListTabView";
import {
    Checkbox,
    DefaultButton,
    Dropdown,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Toggle,
    Label,
} from "office-ui-fabric-react";
import GetIcons from "./GetIcons";
import {DarkModeCalloutStyles} from "./DarkModeCalloutStyles";
import {DocumentSearch} from "../search/DocumentSearch";
import {ApiCallState, NetworkState} from "./ApiCallState";
import {ExternalTeamChannelsSharing} from "./ExternalTeamChannelsSharing";
import {DmsDocumentsContext} from "./components/DmsDocuments";
import {BrowseDocumentsContext} from "./components/BrowseDmsDocuments";
import {Properties} from "./Properties";
import {GetDarkModeStyles} from "../utilities/DarkModeUtilities";
import { TeamsDocumentStatusTabBanner } from "./statustab/banners/TeamsDocumentStatusTabBanner";
import LocationItemsFilters from "./LocationItemsFilters";

export interface IDmsDocSearchTabViewState extends ITeamsBaseComponentState {
    searchPhrase: string;
    searchResults: PaginatedFilingLocations;
    isSearching: boolean;
    hasSearched: boolean;
    teamsId: string;
    channelId: string;
    userId: string;
    tenantId: string;
    checkoutFinished: boolean;
    searchFilter: string;
    ShowNotifyTeamsPanel: boolean;
    NotifyTeamCheckbox: boolean;
    DocumentName: string;
    OptionalNotifyTeamText: string;
    TeamNotified: boolean;
    ShowCheckoutSuccessfulBanner: boolean;
    TeamsFileUrl: string;
    DmsFileId: string;
    NotifyTeamError: boolean;
    CheckoutFailed: boolean;
    ShowCheckoutFailedBanner: boolean;
    shouldIncludeCheckedOutFiles: boolean;
    selectedDocument: FilingLocation;
    selectedFolder: FilingLocation;
    showCheckoutButton: boolean;
    checkingOutDocument: boolean;
    expandedFolderResults: FilingLocation[];
    showSharePanel: boolean;
    sharingDocument: boolean;
    shareSuccessful: boolean;
    showShareButton: boolean;
    showPostShareMessage: boolean;
    shareFailed: boolean;
    searchAllDms: boolean;
    showPropertiesPanel: boolean;
    propertiesList: DmsSearch;
    checkoutFailedMessage: string;
}

export interface IDmsDocSearchTabViewProps {
    caseInfo: DmsCaseInfo;
    darkMode: boolean;
    IsTeamArchived: boolean;
    TeamName: string;
    externalTeamChannels: PaginatedFilingLocations;
    ExternalTeamName: string;
    clientCollabLicensed: boolean;
    externalTeamNameRetrievalState: ApiCallState;
    externalChannelsRetrievalState: ApiCallState;
    isLitigationHoldEnabled: boolean;
    isFrictionlessSpExperienceEnabled: boolean;
    linkType: LinkType;
}

export interface IDmsDocSearchTabViewWithContextProps extends IDmsDocSearchTabViewProps {
    browseDocumentsContext: React.ContextType<typeof BrowseDocumentsContext>;
    documentsContext: React.ContextType<typeof DmsDocumentsContext>;
}

const imOptions: IDropdownOption[] = [
    {key: "title", text: "Title"},
    {key: "author", text: "Author"},
    {key: "number", text: "Number"},
    {key: "keyword", text: "Keyword"},
    {key: "type", text: "Type"},
];

const ndOptions: IDropdownOption[] = [
    {key: "3", text: "Name / Subject"},
    {key: "999", text: "Document ID"},
    {key: "6", text: "Created"},
    {key: "1", text: "Everything"},
];

const spOptions: IDropdownOption[] = [
    {key: "keyword", text: "Keyword"},
    {key: "name", text: "Name"},
    {key: "docid", text: "DocID"},
    {key: "editedby", text: "Edited by"},
    {key: "author", text: "Author"},
    {key: "filetype", text: "File Type"},
];

const WithContextDmsDocSearchTabView =
    (Component: React.ComponentType<IDmsDocSearchTabViewWithContextProps>) => (props: IDmsDocSearchTabViewProps) =>
        (
            // DmsDocSearchTabView consumes two different contexts so must be passed through as props instead of the usual context.XYZ
            <BrowseDocumentsContext.Consumer>
                {(browseDocumentsContext) => (
                    <DmsDocumentsContext.Consumer>
                        {(documentsContext) => (
                            <Component
                                browseDocumentsContext={browseDocumentsContext}
                                documentsContext={documentsContext}
                                {...props}
                            />
                        )}
                    </DmsDocumentsContext.Consumer>
                )}
            </BrowseDocumentsContext.Consumer>
        );

class DmsDocSearchTabViewComponent extends TeamsBaseComponent<
    IDmsDocSearchTabViewWithContextProps,
    IDmsDocSearchTabViewState
> {
    private locationSearch: DocumentSearch = new DocumentSearch();

    constructor(props: Readonly<IDmsDocSearchTabViewWithContextProps>) {
        super(props);
        this.state = {
            searchPhrase: "",
            searchResults: {Page: [], PagingToken: null, Errors: null},
            isSearching: false,
            hasSearched: false,
            theme: null,
            teamsId: this.getQueryVariable("teamsId"),
            channelId: this.getQueryVariable("channelId"),
            userId: this.getQueryVariable("userId"),
            tenantId: this.getQueryVariable("tenantId"),
            checkoutFinished: false,
            searchFilter:
                this.props.caseInfo.StorageType == DmsStorageType.iManage
                    ? "title"
                    : this.props.caseInfo.StorageType == DmsStorageType.NetDocuments
                    ? "3"
                    : "keyword",
            ShowNotifyTeamsPanel: false,
            NotifyTeamCheckbox: false,
            DocumentName: "",
            OptionalNotifyTeamText: "",
            TeamNotified: false,
            ShowCheckoutSuccessfulBanner: false,
            TeamsFileUrl: "",
            DmsFileId: "",
            NotifyTeamError: false,
            CheckoutFailed: false,
            ShowCheckoutFailedBanner: false,
            shouldIncludeCheckedOutFiles: false,
            selectedDocument: null,
            selectedFolder: null,
            showCheckoutButton: false,
            checkingOutDocument: false,
            expandedFolderResults: null,
            showSharePanel: false,
            sharingDocument: false,
            shareSuccessful: false,
            showShareButton: false,
            showPostShareMessage: false,
            shareFailed: false,
            searchAllDms: false,
            showPropertiesPanel: false,
            propertiesList: null,
            checkoutFailedMessage: "Check out failed"
        };

        microsoftTeams.initialize();
    }

    public async componentDidMount(): Promise<void> {
        // If last viewed tab was the DMS tab and the CC tab is reloaded, display the Teams file tab
        window.localStorage.setItem("ContentConnectorViewKey", "teamsfilestab");
        this.props.documentsContext.fetchRoot();
        this.props.documentsContext.setParentState({rootContents: null});
        this.props.browseDocumentsContext.setParentState({documents: {}, caseInfo: this.props.caseInfo, currentFolder: null}, () => {
            this.getRootDocuments();
            if (this.props.browseDocumentsContext.caseInfo.StorageType === DmsStorageType.SharePoint) {
                this.props.browseDocumentsContext.getFilterOptions();
            }
        });
    }

    searchTextChanged = (_: React.ChangeEvent<HTMLInputElement>, val: string) => {
        this.setState({searchPhrase: val}, () => {
            this.runSearch();
        });
    };

    runSearch = () => {
        this.setState({isSearching: true});
        this.props.documentsContext.setParentState({documentsErrorMessage: null});

        this.locationSearch.runThrottledSearch(
            this.state.userId,
            this.state.tenantId,
            this.state.teamsId,
            this.state.channelId,
            this.props.caseInfo.StorageType,
            this.state.searchPhrase,
            false,
            this.state.shouldIncludeCheckedOutFiles,
            this.state.searchAllDms,
            false,
            this.state.searchFilter,
            this.props.browseDocumentsContext.selectedFilters,
            (result) => this.setCompletedSearchState(result),
            (error) => this.setErrorSearchState(error)
        );

        if (!this.state.searchPhrase || this.state.searchPhrase === "") {
            this.setState({
                searchResults: {Page: [], PagingToken: null, Errors: null},
                isSearching: false,
                hasSearched: false,
            });
            this.props.browseDocumentsContext.setParentState({
                currentPath: this.props.caseInfo.LinkedMatterUrl,
                currentFolder: null,
            });
        }
    };

    private setErrorSearchState(error: string) {
        this.props.documentsContext.setParentState({documentsErrorMessage: this.trimErrorMessage(error)});
        return this.setState({
            isSearching: false,
            hasSearched: true,
        });
    }

    private setCompletedSearchState(result: PaginatedFilingLocations) {
        return this.setState({
            searchResults: result,
            isSearching: false,
            hasSearched: true,
            expandedFolderResults: null
        });
    }

    trimErrorMessage = (message: string) => {
        // remove 'Error: ' from the string
        if (message.startsWith("Error: ")) {
            return message.slice(7);
        }
        return message;
    };

    showCheckinPanelAndSelectDoc = (document: FilingLocation) => {
        this.setState({ShowNotifyTeamsPanel: true, DocumentName: document.Name, selectedDocument: document});

        return Promise.resolve();
    };

    showSharePanel = async (documentInfo: DmsSearch, _sharePanel: boolean) => {
        const selectedDocument: FilingLocation = {
            Percent: 0,
            Name: documentInfo.DocName,
            Path: "",
            Url: documentInfo.Url,
            CanFileDirect: false,
            ContentLocationId: 0,
            ContentLocationTitle: "",
            ContentLocationIsExpandable: false,
            IsChildLocation: false,
            Metadata: documentInfo.Metadata,
            StorageType: "",
            AutoMapPath: "",
            AutoMapPlaceholder: "",
            FullPath: "",
            NextPage: "",
            DownloadUrl: "",
            IsFile: true,
            UpdateDocumentVersion: false,
            SiteUrl: "",
            SpHostKVPrefix: "",
            Shared: false,
            ExternalTeamId: "",
            ExternalChannelId: "",
            ChannelType: "",
            DriveId: "",
            TeamsSiteUrl: "",
            CopyFile: false,
            IsUploading: false,
        };

        this.setState({showSharePanel: true, selectedDocument: selectedDocument});
    };

    async checkoutDocument() {
        const document = this.state.selectedDocument;

        this.setState({ShowNotifyTeamsPanel: true, DocumentName: document.Name, checkingOutDocument: true});

        let uploadFileInfo: UploadedFileInfo;

        const checkoutFolderInfo: CheckoutFolderInfo = {
            FolderId: this.state.selectedFolder.Metadata["CheckoutFolderId"],
            FolderUrl: this.state.selectedFolder.Url,
            ChannelId: this.state.selectedFolder.Metadata["ChannelId"],
        };

        await post("/api/teamsdmsintegration/DownloadFileAndUploadToTeam", {
            Url: document.Url,
            Metadata: document.Metadata,
            TeamsId: this.state.teamsId,
            ChannelId: this.state.channelId,
            UserId: this.state.userId,
            TenantId: this.state.tenantId,
            CheckoutFolderInfo: checkoutFolderInfo,
            DriveId: this.state.selectedFolder.DriveId,
            TeamsSiteUrl: this.state.selectedFolder.TeamsSiteUrl,
            CopyFile: this.state.selectedDocument.CopyFile,
            LitigationHoldEnabled: this.props.isLitigationHoldEnabled
        })
            .then((r) => {
                if (r.status.toString().startsWith("2") || r.status.toString().startsWith("409")) {
                    return r.json();
                }
                this.setState({
                    CheckoutFailed: true,
                    ShowCheckoutFailedBanner: true,
                    checkingOutDocument: false,
                    ShowCheckoutSuccessfulBanner: false,
                });
                throw new Error("Search Locations failed");
            })
            .then((r) => {
                if (r.Error === "FileInUseException") 
                {
                    this.setState({
                        CheckoutFailed: true,
                        ShowCheckoutFailedBanner: true,
                        checkingOutDocument: false,
                        ShowCheckoutSuccessfulBanner: false,
                        checkoutFailedMessage: r.ErrorMessage
                    });

                    throw new Error("Checkout failed");
                } else {
                    uploadFileInfo = r;

                    // Users can copy files multiple times to Teams. Don't mark the file as 'checked out' and prevent further copies.
                    if(!this.props.isLitigationHoldEnabled){
                        const rootContents = this.props.documentsContext.rootContents;

                        const searchResults = this.state.searchResults;

                        const expandedFolders = this.state.expandedFolderResults;

                        const docInRootContents = rootContents.Page.filter((i) => i == document);

                        if (docInRootContents.length == 1) {
                            // updates the metadata of the file in the UI, this will either hide the document or disable the check in button
                            docInRootContents[0].Metadata["CheckedOut"] = "True";
                            this.props.documentsContext.setParentState({rootContents: rootContents});
                        } else {
                            const docInSearchResults = searchResults.Page.filter((i) => i == document);

                            if (docInSearchResults.length == 1) {
                                docInSearchResults[0].Metadata["CheckedOut"] = "True";
                                this.setState({searchResults: searchResults});
                            } else {
                                const docInExpandedResults = expandedFolders.filter((i) => i == document);
                                if (docInExpandedResults.length == 1) {
                                    docInExpandedResults[0].Metadata["CheckedOut"] = "True";
                                    this.setState({expandedFolderResults: expandedFolders});
                                }
                            }
                        }
                    }
                    this.setState({
                        TeamsFileUrl: uploadFileInfo.TeamsUrl,
                        DmsFileId: uploadFileInfo.DmsFileId
                    });
                }
            });

        if (this.state.NotifyTeamCheckbox) {
            await this.notifyOfCheckout();
        }

        this.setState({
            checkingOutDocument: false,
            checkoutFinished: true,
            ShowCheckoutSuccessfulBanner: true,
            ShowCheckoutFailedBanner: false,
            CheckoutFailed: false,
        });

        return Promise.resolve();
    }

    async shareDocument() {
        this.setState({sharingDocument: true});

        const metadata = this.state.selectedDocument.Metadata;

        metadata["DmsFileId"] = metadata["SPFileId"]; // ShareDocuemntToExternalTeam expects this to be called 'DmsFileId' need to set the value here.

        const shareFolderInfo: CheckoutFolderInfo = {
            FolderId: this.state.selectedFolder.Metadata["CheckoutFolderId"],
            FolderUrl: this.state.selectedFolder.Url,
            ChannelId: this.state.selectedFolder.Metadata["ChannelId"],
        };

        await post("/api/teamsdmsintegration/ShareDocumentToExternalTeam", {
            Metadata: metadata,
            TeamsId: this.state.teamsId,
            ChannelId: this.state.channelId,
            TenantId: this.state.tenantId,
            ExternalTeamId: this.state.selectedFolder.ExternalTeamId,
            ExternalChannelId: this.state.selectedFolder.ExternalChannelId,
            CheckoutFolderInfo: shareFolderInfo,
        })
            .then((r) => {
                if (r.status.toString().startsWith("2")) {
                    this.setState({shareSuccessful: true, sharingDocument: false, showPostShareMessage: true});
                } else {
                    this.setState({shareFailed: true, sharingDocument: false, showPostShareMessage: true});
                }
            })
            .then((_r) => {
                const document = this.state.selectedDocument;

                const rootContents = this.props.documentsContext.rootContents;

                const searchResults = this.state.searchResults;

                const expandedFolders = this.state.expandedFolderResults;

                const docInRootContents = rootContents.Page.filter((i) => i.Url == document.Url);

                if (docInRootContents.length == 1) {
                    // updates the metadata of the file in the UI, this will either hide the document or disable the check in button
                    docInRootContents[0].Shared = true;
                    this.props.documentsContext.setParentState({rootContents: rootContents});
                }
                if ((searchResults != null && searchResults.Page.length > 0) || expandedFolders != null) {
                    const docInSearchResults = searchResults.Page.filter((i) => i.Url == document.Url);

                    if (docInSearchResults.length == 1) {
                        docInSearchResults[0].Shared = true;
                        this.setState({searchResults: searchResults});
                    } else {
                        const docInExpandedResults = expandedFolders.filter((i) => i.Url == document.Url);
                        if (docInExpandedResults.length == 1) {
                            docInExpandedResults[0].Shared = true;
                            this.setState({expandedFolderResults: expandedFolders});
                        }
                    }
                }
            });
    }

    getSearchPlaceholderText() {
        if (
            this.props.caseInfo.StorageType.toLowerCase() == DmsStorageType.iManage ||
            this.props.caseInfo.StorageType.toLowerCase() == DmsStorageType.NetDocuments ||
            this.props.caseInfo.StorageType.toLowerCase() == DmsStorageType.SharePoint
        ) {
            return "Search for Documents";
        } else {
            return "Search by file name";
        }
    }

    setSearchFilter(val: IDropdownOption) {
        this.setState({searchFilter: val.key.toString()}, () => {
            if (this.state.searchPhrase) {
                this.runSearch();
            }
        });
    }

    setItemTypeFilter(val: IDropdownOption) {
        const alreadySelected =
            this.props.browseDocumentsContext.selectedFilters &&
            this.props.browseDocumentsContext.selectedFilters.some(
                (filter) => filter.DisplayName === val.key.toString()
            );

        if (alreadySelected && val.selected === true) {
            return;
        }

        this.props.documentsContext.setParentState({rootNetworkState: NetworkState.Loading});

        const filterOption = this.props.browseDocumentsContext.filterOptions.find(
            (filterOption: FilterOption) => filterOption.DisplayName === val.key.toString()
        );

        let selectedOptions: FilterOption[];
        if (val.selected === true) {
            selectedOptions = this.props.browseDocumentsContext.selectedFilters
                ? this.props.browseDocumentsContext.selectedFilters.concat(filterOption)
                : [filterOption];
        } else {
            selectedOptions = this.props.browseDocumentsContext.selectedFilters.filter(
                (filter) => filter.DisplayName !== filterOption.DisplayName
            );
        }

        this.props.browseDocumentsContext.setParentState({selectedFilters: selectedOptions}, () => {
            this.props.browseDocumentsContext.filterDocuments({
                TeamsId: this.state.teamsId,
                TenantId: this.state.tenantId,
                UserId: this.state.userId,
                ChannelId: this.state.channelId,
                FolderSearch: this.props.documentsContext.folderSearch,
                BrowsingDms: false,
                FilterParameters: selectedOptions,
                handleResult: (_results: FilingLocation[], _loadedAllPages: boolean) => {
                    const documents = this.props.browseDocumentsContext.getDocumentsByPath(
                        this.props.browseDocumentsContext.getCacheKey(this.props.documentsContext.folderSearch, false)
                    );

                    if (this.props.browseDocumentsContext.currentPath === this.props.caseInfo.LinkedMatterUrl) {
                        this.props.documentsContext.setParentState({
                            rootContents: documents,
                            rootNetworkState: NetworkState.Success,
                        });
                    } else {
                        this.setState({expandedFolderResults: documents.Page}, () => {
                            this.props.documentsContext.setParentState({
                                rootNetworkState: NetworkState.Success,
                            });
                        });
                    }
                },
                handleError: (error) => {
                    this.props.documentsContext.setParentState({
                        rootNetworkState: NetworkState.Error,
                        documentsErrorMessage: this.trimErrorMessage(error),
                        rootContents: {Page: [] as FilingLocation[], PagingToken: null, Errors: null},
                    });
                },
            });
        });
    }

    filteredDataChanged(documents: PaginatedFilingLocations) {
        if (this.props.browseDocumentsContext.currentPath === this.props.caseInfo.LinkedMatterUrl) {
            this.props.documentsContext.setParentState({
                rootContents: documents,
                rootNetworkState: NetworkState.Success,
            });
        } else {
            this.setState({expandedFolderResults: documents.Page}, () => {
                this.props.documentsContext.setParentState({
                    rootNetworkState: NetworkState.Success,
                });
            });
        }
    }

    getSearchOptions() {
        if (this.props.caseInfo.StorageType == DmsStorageType.iManage) {
            return imOptions;
        } else if (this.props.caseInfo.StorageType == DmsStorageType.NetDocuments) {
            return ndOptions;
        } else {
            return spOptions;
        }
    }
    
    getItemTypeOptions() {
        if (this.props.caseInfo.StorageType === DmsStorageType.SharePoint) {
            if (
                this.props.browseDocumentsContext.filterOptions != null &&
                this.props.browseDocumentsContext.filterOptions.length > 0
            ) {
                return this.props.browseDocumentsContext.filterOptions.map((filterOption) => {
                    return {key: filterOption.DisplayName, text: filterOption.DisplayName};
                });
            } else {
                return [{key: "Empty", text: "N/A"}];
            }
        }
    }

    getOpenDmsText() {
        if (this.props.caseInfo.StorageType == DmsStorageType.iManage) {
            return "Open workspace in iManage";
        } else if (this.props.caseInfo.StorageType == DmsStorageType.NetDocuments) {
            return "Open workspace in NetDocuments";
        } else if (this.props.caseInfo.StorageType == DmsStorageType.SharePoint) {
            return "Open document library in SharePoint";
        }
    }

    getFolderViewToggleText() {
        if (this.props.documentsContext.folderSearch) {
            return "Switch to file view";
        } else {
            return "Switch to folder view";
        }
    }

    async notifyOfCheckout() {
        await post("/api/teamsdmsintegration/NotifyTeamOfCheckout", {
            TeamId: this.state.teamsId,
            ChannelId: this.state.selectedFolder.Metadata["ChannelId"],
            TenantId: this.state.tenantId,
            OptionalMessage: this.state.OptionalNotifyTeamText,
            DocumentName: this.state.DocumentName,
            DocUrl: this.state.TeamsFileUrl,
            DmsFileId: this.state.DmsFileId,
        }).then((r) => {
            if (!r.status.toString().startsWith("2")) {
                this.setState({NotifyTeamError: true, TeamNotified: true, NotifyTeamCheckbox: false});
                throw new Error("Failed to notify team");
            }
        });
        this.setState({TeamNotified: true, NotifyTeamCheckbox: false, NotifyTeamError: false});
    }

    openTeamsDocument() {
        microsoftTeams.app.openLink(this.state.TeamsFileUrl);
    }

    folderSelected = (value: boolean, location: FilingLocation) => {
        this.setState({showShareButton: value, selectedFolder: location, showCheckoutButton: value});
    };

    setExpandedResults = (results: FilingLocation[]) => {
        this.setState({expandedFolderResults: results});
    };

    getRootDocuments = () => {
        if (this.props.documentsContext.rootNetworkState === NetworkState.Loading) return;
        const currentFolderSearch = this.props.documentsContext.folderSearch;
        if (
            currentFolderSearch != null &&
            this.props.documentsContext.rootContents != null &&
            !this.props.documentsContext.rootContents.Page.some((x) => x.IsFile != currentFolderSearch)
        ) {
            // show loading spinner for the first time we fetch the other of the file/folder view
            this.props.documentsContext.setParentState({rootNetworkState: NetworkState.Loading});
        }

        const metadata = {GetRootFolders: "Yes"};
        const caseDetails = JSON.parse(this.props.caseInfo.CaseDetails);
       
        this.props.browseDocumentsContext.setParentState({currentPath: this.props.caseInfo.LinkedMatterUrl, currentFolder: null}, () => {
            this.props.browseDocumentsContext.browseDms(
                    {
                        Path: this.props.caseInfo.LinkedMatterUrl,
                        Metadata: metadata,
                        FolderSearch: this.props.documentsContext.folderSearch,
                        StorageType: this.props.caseInfo.StorageType,
                        RootUrl: caseDetails.SiteUrl,
                        TeamsId: this.state.teamsId,
                        TenantId: this.state.tenantId,
                        UserId: this.state.userId,
                        ChannelId: this.state.channelId,
                        BrowsingDms: false,
                        SpHostKVPrefix: caseDetails.SpHostKVPrefix,
                        NextPage: null,
                        UsePaging: true,
                        handleResult: (_results: FilingLocation[], _loadedAllPages: boolean) => {
                            const rootContents = this.props.browseDocumentsContext.getDocumentsByPath(
                                this.props.browseDocumentsContext.getCacheKey(this.props.documentsContext.folderSearch, true)
                            );

                            // if we're currently in the view we made the request for, set Network state to success, else leave unchanged
                            const networkResult =
                                this.props.documentsContext.folderSearch == currentFolderSearch
                                    ? NetworkState.Success
                                    : this.props.documentsContext.rootNetworkState;

                            this.props.documentsContext.setParentState({rootContents, rootNetworkState: networkResult});
                        },
                        handleError: (error) => {
                            this.props.documentsContext.setParentState({
                                rootNetworkState: NetworkState.Error,
                                documentsErrorMessage: this.trimErrorMessage(error),
                                rootContents: {Page: [] as FilingLocation[], PagingToken: null, Errors: null},
                            });
                        },
                        RefreshingView: false,
                    }
                );
            }
        );
    };

    public render() {
        const searchBoxStyles: Partial<ISearchBoxStyles> = {root: {maxWidth: 600}};
        const storageType = this.props.caseInfo.StorageType;

        const bulletedListIcon: IIconProps = {iconName: "BulletedList"};
        const columnListIcon: IIconProps = {iconName: "ColumnList"}; // todo: Need to update package as icon doesnt exit

        if (this.props.IsTeamArchived) {
            return (
                <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
                    The team has been archived, you cannot check out any files to the Team.
                </MessageBar>
            );
        }

        return (
            <div className={this.props.darkMode ? "darkMode" : "lightMode"}>

                {this.props.isLitigationHoldEnabled && (
                    <TeamsDocumentStatusTabBanner
                        isSuccess={false}
                        isError={false}
                        bannerMessage={
                            "Litigation hold enabled for this matter. Files only available to copy."
                        }
                        onDismiss={null}
                    />
                )}

                {this.props.isLitigationHoldEnabled && (
                    <hr style={{height: "10px", visibility: "hidden"}} />
                )}

                <h4 style={{marginBottom: "10px", display: "inline-block"}}>{this.props.caseInfo.CaseName}</h4>
                {this.props.documentsContext.dmsUrl !== "" && (
                    <Icon
                        iconName={"OpenInNewTab"}
                        onClick={() => window.open(this.props.documentsContext.dmsUrl)}
                        style={{
                            marginRight: "5px",
                            flex: "0 1 auto",
                            cursor: "pointer",
                            display: "inline-block",
                            marginLeft: "10px",
                            fontSize: "16px",
                        }}
                        title={this.getOpenDmsText()}
                    />
                )}
                <div style={{marginBottom: "20px", display: "flex"}}>
                    <div style={{width: storageType == DmsStorageType.iManage ? "110px" : "140px"}}>
                        <Dropdown
                            className={this.props.darkMode ? "darkMode" : "lightMode"}
                            styles={this.props.darkMode ? DarkModeCalloutStyles.getDropdownStyle() : null}
                            selectedKey={this.state.searchFilter}
                            onChange={(_, val) => this.setSearchFilter(val)}
                            options={this.getSearchOptions()}
                        />
                    </div>
                    <div style={{width: "400px"}}>
                        <SearchBox
                            className={this.props.darkMode ? "darkMode" : "lightMode"}
                            placeholder={this.getSearchPlaceholderText()}
                            onChange={this.searchTextChanged}
                            autoComplete="off"
                            width="100%"
                            styles={searchBoxStyles}
                        />
                    </div>
                </div>
                <div style={{marginBottom: "15px", display: "flex", flex: 1}}>
                    {this.props.documentsContext.rootNetworkState == NetworkState.Success &&
                        this.props.documentsContext.rootContents !== null &&
                        this.state.expandedFolderResults === null &&
                        this.props.caseInfo.StorageType == DmsStorageType.NetDocuments &&
                        this.props.linkType == LinkType.Workspace && (
                            <div
                                title={this.getFolderViewToggleText()}
                                style={{cursor: "pointer", marginRight: "20px"}}
                            >
                                <DefaultButton
                                    text={
                                        this.props.documentsContext.folderSearch
                                            ? "Switch to List View"
                                            : "Switch to Summary View"
                                    }
                                    iconProps={
                                        this.props.documentsContext.folderSearch ? bulletedListIcon : columnListIcon
                                    }
                                    onClick={() => this._changeView()}
                                />
                            </div>
                        )}

                    {(this.props.caseInfo.StorageType === DmsStorageType.SharePoint &&
                        ((!this.props.documentsContext.folderSearch &&
                        this.props.documentsContext.rootNetworkState !== NetworkState.Loading &&
                        this.props.documentsContext.rootContents !== null) ||
                        this.state.expandedFolderResults !== null)) && (                           
                            <div style={{width: "200px"}} className="inline-block-container">
                                <Label>Filters</Label>

                                <LocationItemsFilters
                                    darkMode={this.props.darkMode}
                                    caseInfo={this.props.caseInfo}
                                    folderSearch={this.props.documentsContext.folderSearch}
                                    teamsId={this.props.documentsContext.teamsId}
                                    tenantId={this.props.documentsContext.tenantId}
                                    userId={this.props.documentsContext.userId}
                                    channelId={this.props.documentsContext.channelId}
                                    searchPhrase={this.state.searchPhrase}
                                    onFilteredDataChanged={(documents: PaginatedFilingLocations) => {
                                        if (this.state.searchPhrase) {
                                            this.runSearch();
                                        }
                                        else{
                                            this.filteredDataChanged(documents);
                                        }
                                    }}
                                />
                            </div>
                        
                    )}

                    {(!this.props.isFrictionlessSpExperienceEnabled &&
                       (((!this.props.documentsContext.folderSearch || this.props.linkType == LinkType.Folder) &&
                        this.props.documentsContext.rootNetworkState !== NetworkState.Loading &&
                        this.props.documentsContext.rootContents !== null) ||
                        this.state.expandedFolderResults !== null)) && (
                        <div>
                            <Toggle
                                inlineLabel
                                checked={this.state.shouldIncludeCheckedOutFiles}
                                onChange={(_, val) => {
                                    this.setState({shouldIncludeCheckedOutFiles: val}, () => {
                                        // Don't run search if user clicks toggle while browsing a subfolder or hasn't set a search string.
                                        if(!this.state.expandedFolderResults && this.state.searchPhrase) {
                                            this.runSearch();
                                        }
                                    });
                                }}
                                label="Show checked out files"
                            />
                        </div>
                    )}
                </div>

                {this.props.caseInfo.StorageType == DmsStorageType.iManage && (
                    <div>
                        <Toggle
                            inlineLabel
                            checked={this.state.searchAllDms}
                            onChange={(_, val) => {
                                this.setState({searchAllDms: val}, () => {
                                    this.runSearch();
                                });
                            }}
                            label="Search iManage™ library"
                        />
                    </div>
                )}

                {this._getContent()}

                {this.state.showPropertiesPanel && (
                    <Properties
                        darkMode={GetDarkModeStyles(this.props.darkMode)}
                        showPropertiesPanel={this.state.showPropertiesPanel}
                        ClosePanel={() => this.setState({showPropertiesPanel: false})}
                        propertiesList={this.state.propertiesList}
                        storageType={this.props.caseInfo.StorageType}
                        tabType={TabType.Dms}
                    />
                )}

                <div>
                    {this.props.documentsContext.documentsErrorMessage != null && (
                        <div>
                            <p style={{color: "red"}}>{this.props.documentsContext.documentsErrorMessage}</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    public async _changeView() {
        if (this.props.documentsContext.folderSearch) {
            await this.props.documentsContext.setParentState({folderSearch: false});
        } else {
            await this.props.documentsContext.setParentState({folderSearch: true});
        }

        const folderSearch = this.props.documentsContext.folderSearch;

        window.localStorage.setItem("NetDocsFolderSearch", folderSearch.toString());

        await this.getRootDocuments();
    }

    public _getContent() {
        if (this.state.hasSearched || this.state.isSearching) return this._getSearchResults();
        else return this._getFolderContents();
    }

    public _getFolderContents() {
        if (
            this.props.documentsContext.rootNetworkState == NetworkState.Loading ||
            this.props.documentsContext.rootContents === null
        )
            return (
                <div>
                    <Spinner className={this.props.darkMode ? "darkMode" : "lightMode"} label="Loading contents..." />
                </div>
            );

        const cacheKey = this.props.browseDocumentsContext.getCacheKey(this.props.documentsContext.folderSearch, false);


        return (
            <div>
                {!this.state.ShowNotifyTeamsPanel &&
                    this.props.browseDocumentsContext.isListLimited(cacheKey, this.props.caseInfo.StorageType) && (
                        <MessageBar isMultiline={true} messageBarType={MessageBarType.warning}>
                            <div style={{fontSize: "14px"}}>
                                {
                                    "This location contains more than 5,000 items and features such as sorting may not work correctly."
                                }
                                <a
                                    href="https://support.intapp.com/hc/en-us/sections/4975173994013-Product-Documentation"
                                    target="_blank"
                                >
                                    Click here to learn more
                                </a>
                            </div>
                        </MessageBar>
                    )}
                <div>
                    <LocationItemsListTabView
                        onChooseLocation={this.showCheckinPanelAndSelectDoc}
                        chooseThisLocationText={"File Here"}
                        locations={this.props.documentsContext.rootContents}
                        isSearching={this.state.isSearching}
                        onExpand={() => {}}
                        showResults={true}
                        searchPhrase={null}
                        moreResults={false}
                        folderSearch={false}
                        localDocs={false}
                        teamsId={this.state.teamsId}
                        channelId={this.state.channelId}
                        storageType={this.props.caseInfo.StorageType}
                        userId={this.state.userId}
                        tenantId={this.state.tenantId}
                        errorMessage={this.props.documentsContext.documentsErrorMessage}
                        TabView={true}
                        ShowCheckinPanel={this.showSharePanel}
                        noItemsText="The matter area you are connected to is currently empty"
                        expandedResults={null}
                        onBack={() => this.setState({expandedFolderResults: null})}
                        darkMode={this.props.darkMode}
                        searchFilter={this.state.searchFilter}
                        checkoutToChannel={false}
                        ShowFilingButton={null}
                        setExpandedResults={this.setExpandedResults}
                        expandedFolderResults={this.state.expandedFolderResults}
                        shouldIncludeCheckedOutFiles={this.state.shouldIncludeCheckedOutFiles}
                        clientCollabLicensed={this.props.clientCollabLicensed}
                        searchAllDms={this.state.searchAllDms}
                        EllipsisOptionOnClick={this.openPropertiesPanel}
                        privateChannel={false}
                        isLitigationHoldEnabled={this.props.isLitigationHoldEnabled}
                        isFrictionlessSpExperienceEnabled={this.props.isFrictionlessSpExperienceEnabled}
                    />
                </div>
                {this._getShowNotifyPanel()}
                {this._getSharePanel()}
            </div>
        );
    }

    openPropertiesPanel = (documentInfo: DmsSearch, option: EllipsisOptions) => {
        if (option == EllipsisOptions.Properties) {
            this.setState({showPropertiesPanel: true, propertiesList: documentInfo});
        }
    };

    public _getSearchResults() {
        return (
            <div>
                <div style={{marginTop: "20px"}}>
                    <span>Search Results</span>
                    <LocationItemsListTabView
                        onChooseLocation={this.showCheckinPanelAndSelectDoc}
                        chooseThisLocationText={"File Here"}
                        locations={this.state.searchResults}
                        isSearching={this.state.isSearching}
                        onExpand={() => {}}
                        showResults={this.state.hasSearched}
                        searchPhrase={this.state.searchPhrase}
                        moreResults={true}
                        folderSearch={false}
                        localDocs={false}
                        teamsId={this.state.teamsId}
                        channelId={this.state.channelId}
                        storageType={this.props.caseInfo.StorageType}
                        userId={this.state.userId}
                        tenantId={this.state.tenantId}
                        errorMessage={this.props.documentsContext.documentsErrorMessage}
                        TabView={true}
                        ShowCheckinPanel={this.showSharePanel}
                        expandedResults={null}
                        onBack={null}
                        darkMode={this.props.darkMode}
                        searchFilter={this.state.searchFilter}
                        checkoutToChannel={false}
                        ShowFilingButton={null}
                        setExpandedResults={() => {}}
                        expandedFolderResults={null}
                        shouldIncludeCheckedOutFiles={this.state.shouldIncludeCheckedOutFiles}
                        clientCollabLicensed={this.props.clientCollabLicensed}
                        searchAllDms={this.state.searchAllDms}
                        EllipsisOptionOnClick={this.openPropertiesPanel}
                        privateChannel={false}
                        isLitigationHoldEnabled={this.props.isLitigationHoldEnabled}
                        isFrictionlessSpExperienceEnabled={this.props.isFrictionlessSpExperienceEnabled}
                    />
                </div>
                {this._getShowNotifyPanel()}
                {this._getSharePanel()}
            </div>
        );
    }

    public _getSharePanel() {
        let iconDetails: string[] = ["", ""];

        let documentName = "";

        if (this.state.selectedDocument != null) {
            iconDetails = GetIcons.getIcon(this.state.selectedDocument, this.props.darkMode);
            documentName = this.state.selectedDocument.Name;
        }

        return (
            <Panel
                headerText={"Share document"}
                className={this.props.darkMode ? "darkMode" : "lightMode"}
                isOpen={this.state.showSharePanel}
                onDismiss={() =>
                    this.setState({
                        showSharePanel: false,
                        shareSuccessful: false,
                        showShareButton: false,
                        showCheckoutButton: false,
                        showPostShareMessage: false,
                    })
                }
                closeButtonAriaLabel="Close"
                customWidth="450px"
                type={PanelType.custom}
            >
                <hr style={{height: "10px", visibility: "hidden"}} />
                <div>
                    {(this.state.shareSuccessful || this.state.shareFailed) && this.state.showPostShareMessage && (
                        <MessageBar
                            messageBarType={this.state.shareSuccessful ? MessageBarType.success : MessageBarType.error}
                            isMultiline={true}
                            onDismiss={
                                this.state.shareSuccessful ? null : () => this.setState({showPostShareMessage: false})
                            }
                        >
                            <div style={{fontSize: "14px"}}>
                                {this.state.shareSuccessful ? "File shared successfully" : "Failed to share file"}
                            </div>
                        </MessageBar>
                    )}

                    {this.state.ShowCheckoutFailedBanner && this.state.CheckoutFailed && (
                        <div>
                            <MessageBar
                                messageBarType={MessageBarType.error}
                                isMultiline={true}
                                onDismiss={() => this.setState({ShowCheckoutFailedBanner: false})}
                            >
                                <div style={{fontSize: "14px"}}>{"Check out failed"}</div>
                            </MessageBar>
                            <hr style={{height: "10px", visibility: "hidden"}} />
                        </div>
                    )}

                    {this.state.shareSuccessful && <hr style={{height: "10px", visibility: "hidden"}} />}

                    <div className="metadataWrapper">
                        <div className="checkoutIcon">
                            <Icon
                                iconName={iconDetails[0]}
                                style={{fontSize: "24px", marginTop: "6px", color: iconDetails[1]}}
                            />
                        </div>
                        <div className="checkoutFileName preWrap">
                            <p>{documentName}</p>
                        </div>
                    </div>

                    <hr style={{height: "10px", visibility: "hidden"}} />

                    {!this.state.checkoutFinished && !this.state.shareSuccessful && (
                        <ExternalTeamChannelsSharing
                            externalTeamName={this.props.ExternalTeamName}
                            externalTeamNameRetrievalState={this.props.externalTeamNameRetrievalState}
                            externalChannels={this.props.externalTeamChannels}
                            externalChannelsRetrievalState={this.props.externalChannelsRetrievalState}
                            channelId={
                                this.props.externalTeamChannels != null
                                    ? this.props.externalTeamChannels.Page[0].ExternalChannelId
                                    : this.state.channelId
                            }
                            teamsId={this.state.teamsId}
                            tenantId={this.state.tenantId}
                            userId={this.state.userId}
                            storageType={this.props.caseInfo.StorageType}
                            searchFilter={this.state.searchFilter}
                            isClientCollabLicensed={this.props.clientCollabLicensed}
                            isDarkMode={this.props.darkMode}
                            onChooseLocation={this.showCheckinPanelAndSelectDoc}
                            showFilingButton={this.folderSelected}
                        />
                    )}

                    {this.state.showShareButton && !this.state.shareSuccessful && (
                        <p>The file will be checked out to the linked workspace for Client Collaboration</p>
                    )}

                    <hr style={{height: "10px", visibility: "hidden"}} />

                    {this.state.sharingDocument && (
                        <div>
                            <Spinner />
                        </div>
                    )}
                    {!this.state.shareSuccessful && 
                        !this.props.isLitigationHoldEnabled &&
                        !this.state.sharingDocument && 
                        this.state.showShareButton && (
                        <div>
                            <PrimaryButton
                                style={{width: "125px"}}
                                onClick={() => this.shareDocument()}
                                text={"Share"}
                            />
                        </div>
                    )}

                    {this.state.shareSuccessful && (
                        <div>
                            <PrimaryButton
                                style={{width: "125px"}}
                                onClick={() =>
                                    this.setState({
                                        showSharePanel: false,
                                        shareSuccessful: false,
                                        showShareButton: false,
                                        showCheckoutButton: false,
                                    })
                                }
                                text={"Ok"}
                            />
                        </div>
                    )}
                </div>

                {this.state.CheckoutFailed && (
                    <div>
                        <hr style={{height: "10px", visibility: "hidden"}} />

                        <div>
                            <DefaultButton
                                onClick={() =>
                                    this.setState({
                                        ShowNotifyTeamsPanel: false,
                                        checkoutFinished: false,
                                        OptionalNotifyTeamText: "",
                                        NotifyTeamCheckbox: false,
                                        TeamNotified: false,
                                        CheckoutFailed: false,
                                        ShowCheckoutSuccessfulBanner: false,
                                    })
                                }
                                text="Check out more"
                            />
                        </div>
                    </div>
                )}
            </Panel>
        );
    }

    public _getShowNotifyPanel() {
        const notifyIcon = {iconName: "Send"};
        const notifyFailureIcon = {iconName: "Error"};

        let iconDetails: string[] = ["", ""];

        let documentName = "";

        let panelText = this.props.isLitigationHoldEnabled ? "Copy to Teams" : "Check Out to Teams";
        let buttonText = this.props.isLitigationHoldEnabled ? "Copy" : "Check Out";
        let successText = this.props.isLitigationHoldEnabled ? "Copy successful" : "Check out successful";
        let failedText = this.props.isLitigationHoldEnabled ? "Copy failed" : this.state.checkoutFailedMessage;
        let finishedText = this.props.isLitigationHoldEnabled ? "Copy more" : "Check out more";

        let hideText = true;

        if (this.state.selectedDocument != null) {
            iconDetails = GetIcons.getIcon(this.state.selectedDocument, this.props.darkMode);
            documentName = this.state.selectedDocument.Name;

            if (this.state.selectedDocument.CopyFile) {
                panelText = "Copy file to Teams";
                buttonText = "Copy";
                successText = "Copy successful";
                failedText = "Copy failed";
                hideText = false;
                finishedText = "Copy more";
            }
        }

        return (
            <Panel
                headerText={panelText}
                className={this.props.darkMode ? "darkMode" : "lightMode"}
                isOpen={this.state.ShowNotifyTeamsPanel}
                onDismiss={() =>
                    this.setState({
                        ShowNotifyTeamsPanel: false,
                        checkoutFinished: false,
                        OptionalNotifyTeamText: "",
                        NotifyTeamCheckbox: false,
                        TeamNotified: false,
                        CheckoutFailed: false,
                        ShowCheckoutSuccessfulBanner: false,
                        showShareButton: false,
                        showCheckoutButton: false,
                    })
                }
                closeButtonAriaLabel="Close"
                customWidth="450px"
                type={PanelType.custom}
            >
                {!hideText && !this.state.ShowCheckoutSuccessfulBanner && (
                    <div>
                        <hr style={{height: "20px", visibility: "hidden"}} />
                        <MessageBar>
                            <div style={{fontSize: "14px"}}>
                                Files will be copied from the workspace to Teams, but won't be checked out.
                            </div>
                        </MessageBar>
                    </div>
                )}

                {(this.state.ShowCheckoutSuccessfulBanner || this.state.TeamNotified) && (
                    <hr style={{height: "10px", visibility: "hidden"}} />
                )}

                {this.state.ShowCheckoutSuccessfulBanner && (
                    <MessageBar messageBarType={MessageBarType.success} isMultiline={true}>
                        <div style={{fontSize: "14px"}}>{successText}</div>
                    </MessageBar>
                )}

                {this.state.TeamNotified && !this.state.NotifyTeamError && (
                    <MessageBar
                        messageBarType={MessageBarType.success}
                        isMultiline={true}
                        messageBarIconProps={notifyIcon}
                    >
                        <div style={{fontSize: "14px"}}>{"Team notified"}</div>
                    </MessageBar>
                )}

                {this.state.TeamNotified && this.state.NotifyTeamError && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={true}
                        messageBarIconProps={notifyFailureIcon}
                        onDismiss={() => this.setState({TeamNotified: false})}
                    >
                        <div style={{fontSize: "14px"}}>{"Failed to notify Team"}</div>
                    </MessageBar>
                )}

                <hr style={{height: "10px", visibility: "hidden"}} />
                <div>
                    <div className="metadataWrapper">
                        <div className="checkoutIcon">
                            <Icon
                                iconName={iconDetails[0]}
                                style={{fontSize: "24px", marginTop: "6px", color: iconDetails[1]}}
                            />
                        </div>
                        <div className="checkoutFileName preWrap">
                            <p>{documentName}</p>
                        </div>
                    </div>

                    <hr style={{height: "10px", visibility: "hidden"}} />

                    {!this.state.checkoutFinished && (
                        <div>
                            <p style={{fontSize: "16px"}}>
                                {this.props.TeamName} {">"} Channels
                            </p>
                            {this.props.documentsContext.teamsChannelNetworkState == NetworkState.Loading && (
                                <div>
                                    <hr style={{height: "10px", visibility: "hidden"}} />
                                    <Spinner size={SpinnerSize.medium} label="Loading channels..." />
                                </div>
                            )}

                            {this.props.documentsContext.teamsChannelNetworkState == NetworkState.Error && (
                                <p style={{color: "red", fontSize: "16px", marginTop: "10px"}}>
                                    Teams Channels Retrieval Failed. If this Team was recently created try reloading the
                                    tab.
                                </p>
                            )}

                            {this.props.documentsContext.teamsChannelNetworkState == NetworkState.Success && (
                                <LocationItemsListTabView
                                    onChooseLocation={this.showCheckinPanelAndSelectDoc}
                                    chooseThisLocationText={"File Here"}
                                    locations={this.props.documentsContext.teamChannels}
                                    isSearching={this.state.isSearching}
                                    onExpand={() => {}}
                                    showResults={true}
                                    searchPhrase={null}
                                    moreResults={false}
                                    folderSearch={false}
                                    localDocs={false}
                                    teamsId={this.state.teamsId}
                                    channelId={this.state.channelId}
                                    storageType={this.props.caseInfo.StorageType}
                                    userId={this.state.userId}
                                    tenantId={this.state.tenantId}
                                    errorMessage={this.props.documentsContext.documentsErrorMessage}
                                    TabView={true}
                                    ShowCheckinPanel={() => {}}
                                    noItemsText="The matter area you are connected to is currently empty"
                                    expandedResults={null}
                                    onBack={null}
                                    darkMode={this.props.darkMode}
                                    searchFilter={this.state.searchFilter}
                                    checkoutToChannel={true}
                                    ShowFilingButton={this.folderSelected}
                                    expandedFolderResults={null}
                                    setExpandedResults={() => {}}
                                    shouldIncludeCheckedOutFiles={this.state.shouldIncludeCheckedOutFiles}
                                    clientCollabLicensed={this.props.clientCollabLicensed}
                                    searchAllDms={this.state.searchAllDms}
                                    EllipsisOptionOnClick={this.openPropertiesPanel}
                                    privateChannel={false}
                                    isLitigationHoldEnabled={this.props.isLitigationHoldEnabled}
                                    isFrictionlessSpExperienceEnabled={this.props.isFrictionlessSpExperienceEnabled}
                                />
                            )}
                        </div>
                    )}

                    {!this.state.TeamNotified &&
                        !this.state.checkoutFinished &&
                        this.props.documentsContext.teamsChannelNetworkState == NetworkState.Success && (
                            <div className="notifyTeamLabelFontSize">
                                <Checkbox
                                    checked={this.state.NotifyTeamCheckbox}
                                    onChange={(_, val) => this.setState({NotifyTeamCheckbox: val})}
                                    label="Post to the channel about this document"
                                />
                                <hr style={{height: "10px", visibility: "hidden"}} />
                            </div>
                        )}

                    {this.state.NotifyTeamCheckbox && (
                        <div>
                            <TextField
                                multiline={true}
                                onChange={(_, val) => this.setState({OptionalNotifyTeamText: val})}
                                autoComplete="off"
                                style={{
                                    backgroundColor: this.props.darkMode ? "black" : "white",
                                    color: this.props.darkMode ? "white" : "black",
                                }}
                                placeholder="Optional comment..."
                                value={this.state.OptionalNotifyTeamText}
                            />

                            <hr style={{height: "10px", visibility: "hidden"}} />
                        </div>
                    )}

                    {this.state.checkoutFinished && (
                        <div style={{marginTop: this.state.TeamNotified ? "15px" : ""}}>
                            <DefaultButton
                                onClick={() =>
                                    this.setState({
                                        ShowNotifyTeamsPanel: false,
                                        checkoutFinished: false,
                                        OptionalNotifyTeamText: "",
                                        NotifyTeamCheckbox: false,
                                        TeamNotified: false,
                                        ShowCheckoutSuccessfulBanner: false,
                                    })
                                }
                                text={finishedText}
                            />
                            <PrimaryButton
                                style={{marginLeft: "10px"}}
                                onClick={() => this.openTeamsDocument()}
                                text="Open File"
                            />
                        </div>
                    )}

                    <hr style={{height: "10px", visibility: "hidden"}} />

                    {this.state.checkingOutDocument && (
                        <div>
                            <Spinner />
                        </div>
                    )}
                    {this.state.showCheckoutButton &&
                        !this.state.checkingOutDocument &&
                        !this.state.checkoutFinished && (
                            <div>
                                <PrimaryButton
                                    style={{width: "125px"}}
                                    onClick={() => this.checkoutDocument()}
                                    text={buttonText}
                                />
                            </div>
                        )}
                </div>

                {this.state.CheckoutFailed && (
                    <div>
                        <hr style={{height: "10px", visibility: "hidden"}} />
                        {this.state.ShowCheckoutFailedBanner && (
                            <MessageBar
                                messageBarType={MessageBarType.error}
                                isMultiline={true}
                                onDismiss={() => this.setState({ShowCheckoutFailedBanner: false})}
                            >
                                <div style={{fontSize: "14px"}}>{failedText}</div>
                            </MessageBar>
                        )}

                        <hr style={{height: "10px", visibility: "hidden"}} />

                        <div>
                            <DefaultButton
                                onClick={() =>
                                    this.setState({
                                        ShowNotifyTeamsPanel: false,
                                        checkoutFinished: false,
                                        OptionalNotifyTeamText: "",
                                        NotifyTeamCheckbox: false,
                                        TeamNotified: false,
                                        CheckoutFailed: false,
                                        ShowCheckoutSuccessfulBanner: false,
                                    })
                                }
                                text={finishedText}
                            />
                        </div>
                    </div>
                )}
            </Panel>
        );
    }
}

export const DmsDocSearchTabView = WithContextDmsDocSearchTabView(DmsDocSearchTabViewComponent);
